'use client';

// Public status badge for the landing page. Polls the gateway's
// /_status endpoint (fronted by edge-LB) every 30s and renders a
// summary tone. Visitor-confidence signal — must NOT block first
// paint or fail the page if the endpoint is unreachable; an
// indeterminate "checking…" state is fine on initial render.

import { useEffect, useState } from 'react';

interface StatusJson {
  ok:     boolean;
  as_of:  string;
  // {bchn: {regtest: 'ok'|'degraded', ...}, fulcrum: {...}, chaingraph: {...}}
  systems: Record<string, Record<string, 'ok' | 'degraded'>>;
}

type FetchState =
  | { kind: 'pending' }
  | { kind: 'ok';      data: StatusJson }
  | { kind: 'error' };

const POLL_INTERVAL_MS = 30_000;

export function StatusBadge() {
  const [state, setState] = useState<FetchState>({ kind: 'pending' });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function refresh() {
      try {
        const res = await fetch('/_status', { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json() as StatusJson;
        if (!cancelled) setState({ kind: 'ok', data });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    }

    void refresh();
    timer = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);

    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, []);

  // Map state → tone + label. Three buckets only — visitor-friendly
  // copy beats fine-grained per-system text in this position.
  let tone:    'pending' | 'ok' | 'degraded' | 'error';
  let label:   string;
  let title:   string | undefined;

  if (state.kind === 'pending') {
    tone  = 'pending';
    label = 'Checking status…';
  } else if (state.kind === 'error') {
    tone  = 'error';
    label = 'Status unavailable';
    title = 'Could not reach the status endpoint.';
  } else if (state.data.ok) {
    tone  = 'ok';
    label = 'All systems operational';
    title = `As of ${new Date(state.data.as_of).toLocaleString()}`;
  } else {
    tone  = 'degraded';
    // Surface which (system, network) pairs are degraded in the title;
    // the visible label stays short.
    const degraded: string[] = [];
    for (const [sys, nets] of Object.entries(state.data.systems)) {
      for (const [net, st] of Object.entries(nets)) {
        if (st === 'degraded') degraded.push(`${sys}/${net}`);
      }
    }
    label = degraded.length === 0
      ? 'No backends configured'
      : `Some endpoints degraded`;
    title = degraded.length > 0 ? `Degraded: ${degraded.join(', ')}` : undefined;
  }

  const dotCls = tone === 'ok'        ? 'bg-emerald-400'
              : tone === 'degraded'   ? 'bg-amber-400'
              : tone === 'error'      ? 'bg-red-400'
              :                         'bg-ink-faint animate-pulse';

  const labelCls = tone === 'ok'      ? 'text-emerald-300'
                : tone === 'degraded' ? 'text-amber-300'
                : tone === 'error'    ? 'text-red-300'
                :                       'text-ink-faint';

  return (
    <span
      className="inline-flex items-center gap-2 text-xs"
      title={title}
    >
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      <span className={labelCls}>{label}</span>
    </span>
  );
}
